// 80mm receipt through the browser / WebView print dialog (AirPrint on iOS).
// Native thermal printers (Star / ESC-POS from PetPOS) plug in here later.
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const fmt = (n, sym) => {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return (v < 0 ? "-" : "") + sym + Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 });
};

const METHOD_NAMES = { cash: "現金", wallet: "錢包", points: "積分", exchange_credit: "換貨抵扣" };
export const methodLabel = (p) => p.method_name || p.methodName || METHOD_NAMES[p.method] || p.method;

// The saved name is data (what the receipt prints). On screen, the built-in
// names follow the language of the app; a shop's own method names stay as typed.
import { zhEn } from "./posTier.js";
const BUILTIN_EN = { "現金": "Cash", "錢包": "Wallet", "積分": "Points", "換貨抵扣": "Exchange credit" };
export const payLabel = (name) => zhEn(name, BUILTIN_EN[name] ?? name);

// Normalise a sales row (with sale_lines + sale_payments) into receipt data.
export const receiptFromSale = (sale, customerName) => ({
  number: sale.number,
  kind: sale.kind,
  at: sale.created_at,
  customerName,
  lines: (sale.sale_lines || []).map(l => ({ name: l.name, qty: Number(l.qty), unit: Number(l.unit_price), discount: Number(l.discount), total: Number(l.total), original: l.original_price == null ? null : Number(l.original_price), promoName: l.meta?.promoName || null })),
  orderDiscount: Number(sale.order_discount) || 0,
  total: Number(sale.total),
  payments: (sale.sale_payments || []).map(p => ({ name: methodLabel(p), amount: Number(p.amount), tendered: p.tendered == null ? null : Number(p.tendered), change: Number(p.change_given) || 0, points: p.points })),
  change: Number(sale.change_due) || 0,
  pointsEarned: Number(sale.points_earned) || 0,
  note: sale.note || "",
});

// 交更報告 (Z report) — a closed pos_shifts row with its summary snapshot.
export function printShiftReport(sh, { tenant, sym = "$" } = {}) {
  const f = sh.summary || {};
  const t = (iso) => iso ? new Date(iso).toLocaleString("zh-HK") : "—";
  const row = (l, v, b) => `<tr${b ? ' class="tot"' : ""}><td>${esc(l)}</td><td class="r">${v}</td></tr>`;
  const NAMES = { cash: "現金", wallet: "錢包", points: "積分" };
  const methods = Object.entries(f.byMethod || {}).map(([k, v]) => row("　" + (NAMES[k] || k), fmt(v, sym))).join("");
  const moves = (sh.cash_moves || []).map(m => row("　" + (m.type === "in" ? "放入 " : "取出 ") + (m.note || ""), (m.type === "in" ? "+" : "-") + fmt(m.amount, sym))).join("");
  const diff = Number(sh.difference) || 0;
  printHtml(`<h1>${esc(tenant?.name || "")}</h1><p class="c"><b>交更報告</b><br><span class="m">${t(sh.opened_at)} → ${t(sh.closed_at)}</span></p><hr>
  <table>${row("單數", f.sales ?? 0)}${row("淨銷售", fmt(f.netSales, sym))}${Number(f.refunds) ? row("退款", "-" + fmt(f.refunds, sym)) : ""}${methods}</table><hr>
  <table>${row("備用金", fmt(sh.opening_float, sym))}${row("收銀現金", fmt(f.posCash, sym))}${Number(f.aptCash) ? row("預約收款現金", fmt(f.aptCash, sym)) : ""}${moves}
  ${row("應有現金", fmt(sh.expected_cash, sym), true)}${row("點到現金", fmt(sh.counted_cash, sym), true)}${row(diff === 0 ? "啱數" : diff > 0 ? "多" : "少", fmt(Math.abs(diff), sym), true)}</table>
  ${sh.note ? `<hr><p>備註：${esc(sh.note)}</p>` : ""}`, "交更報告");
}

function printHtml(body, title) {
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>
  @page { size: 80mm auto; margin: 4mm } body { font: 12px/1.45 -apple-system, "PingFang HK", "Noto Sans TC", sans-serif; color: #111; width: 72mm; margin: 0 auto }
  h1 { font-size: 16px; text-align: center; margin: 0 } .c { text-align: center } .m { color: #555; font-size: 11px } .r { text-align: right; white-space: nowrap }
  table { width: 100%; border-collapse: collapse } td { padding: 1px 0 } hr { border: 0; border-top: 1px dashed #999; margin: 6px 0 } .tot td { font-size: 14px; font-weight: 800 }
  </style></head><body>${body}</body></html>`;
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
  setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* blocked */ } setTimeout(() => iframe.remove(), 60000); }, 250);
}

export function printReceipt(r, { tenant, sym = "$" } = {}) {
  const ps = tenant?.posSettings || {};
  const when = r.at ? new Date(r.at) : new Date();
  const title = r.kind === "refund" ? "退款單" : r.kind === "exchange" ? "換貨單" : "收據";
  const rows = r.lines.map(l => `
    <tr><td colspan="2" class="n">${esc(l.name)}</td></tr>
    ${l.promoName ? `<tr><td colspan="2" class="m">[促銷 ${esc(l.promoName)}]</td></tr>` : ""}
    <tr><td class="m">${l.qty} × ${fmt(l.unit, sym)}${l.discount ? ` <span class="d">-${fmt(l.discount, sym)}</span>` : ""}${l.original != null && l.original !== l.unit ? ` <span class="d">(原價 ${fmt(l.original, sym)})</span>` : ""}</td><td class="r">${fmt(l.total, sym)}</td></tr>`).join("");
  const pays = r.payments.map(p => `<tr><td>${esc(p.name)}${p.points ? ` (${p.points} 分)` : ""}</td><td class="r">${fmt(p.amount, sym)}</td></tr>${p.tendered != null ? `<tr><td class="m">　收</td><td class="r m">${fmt(p.tendered, sym)}</td></tr>` : ""}`).join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)} #${r.number}</title>
<style>
  @page { size: 80mm auto; margin: 4mm }
  body { font: 12px/1.45 -apple-system, "PingFang HK", "Noto Sans TC", sans-serif; color: #111; width: 72mm; margin: 0 auto }
  h1 { font-size: 16px; text-align: center; margin: 0 }
  .c { text-align: center } .m { color: #555; font-size: 11px } .d { color: #555 } .r { text-align: right; white-space: nowrap }
  table { width: 100%; border-collapse: collapse } td { padding: 1px 0; vertical-align: top } .n { font-weight: 700; padding-top: 4px }
  hr { border: 0; border-top: 1px dashed #999; margin: 6px 0 } .tot td { font-size: 15px; font-weight: 800 }
</style></head><body>
  <h1>${esc(tenant?.name || "")}</h1>
  ${tenant?.address ? `<p class="c m">${esc(tenant.address)}</p>` : ""}${tenant?.phone ? `<p class="c m">${esc(tenant.phone)}</p>` : ""}
  <hr><p class="c"><b>${esc(title)} #${esc((ps.receiptPrefix || "") + r.number)}</b><br><span class="m">${when.toLocaleString("zh-HK")}</span>${r.customerName ? `<br>客戶：${esc(r.customerName)}` : ""}</p><hr>
  <table>${rows}</table><hr>
  <table>
    ${r.orderDiscount ? `<tr><td>整單折扣</td><td class="r">-${fmt(r.orderDiscount, sym)}</td></tr>` : ""}
    <tr class="tot"><td>合計</td><td class="r">${fmt(r.total, sym)}</td></tr>
    ${pays}
    ${r.change > 0 ? `<tr><td><b>找續</b></td><td class="r"><b>${fmt(r.change, sym)}</b></td></tr>` : ""}
  </table>
  ${r.pointsEarned ? `<hr><p class="c">${r.pointsEarned > 0 ? "今次賺取" : "扣回"} ${Math.abs(r.pointsEarned)} 積分</p>` : ""}
  ${ps.receiptFooter ? `<hr><p class="c">${esc(ps.receiptFooter)}</p>` : ""}
  <p class="c m" style="margin-top:8px">多謝惠顧 🐾</p>
</body></html>`;
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
  setTimeout(() => {
    try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* blocked */ }
    setTimeout(() => iframe.remove(), 60000);
  }, 250);
}
